import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { useLocation, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef } from "react";
import Comment from "../components/Comment";
import { createComment, loadComments, removeComment, updateComment } from "toolkit/comments/comments.thunks";

function getIndex(element: any) {
	let index = 1;
	let sibling = element.previousElementSibling;
	while (sibling) {
		index++;
		sibling = sibling.previousElementSibling;
	}
	return index;
}
function escapeSpecialChars(str: string): string {
	const specialChars: string[] = ["*", "+", "?", ".", "(", ")", "[", "]", "{", "}", "|", "^", "$"];
	const escapedChars: string[] = specialChars.map((char: string) => {
		return "\\" + char;
	});
	const regex: RegExp = new RegExp(`[${escapedChars.join("")}]`, "g");
	return str.replace(regex, (match) => "\\" + match);
}

export function useComments() {
	const dispatch = useAppDispatch();
	const location = useLocation();

	const params = useParams();

	const comments = useAppSelector((state) => state.comments);
	const admin = useAppSelector((state) => state.profile.profile.admin);

	const firstLoad = useRef(false);

	const timer = useRef<null | NodeJS.Timeout>(null);

	const path = useMemo(() => {
		let strPath = location.pathname;
		let arrParams = Object.keys(params);
		arrParams.forEach((_) => {
			if (_ !== "stepId" && _ !== "*") {
				const regExp = new RegExp(`/${params[_]}`);
				strPath = strPath.replace(regExp, "");
			}
		});
		return strPath;
		//eslint-disable-next-line
	}, [location.pathname]);

	const replaceComment = useCallback(
		(e?: any) => {
			const wrapperComment = document.body.querySelector("#wrapper-comment");

			if (wrapperComment && e && !(e?.target instanceof Window) && wrapperComment.contains(e.target)) return;
			Comment.removeAll();
			if (!comments.visible) return;
			if (timer.current) {
				clearTimeout(timer.current);
			}
			timer.current = setTimeout(() => {
				comments.items.forEach((_) => {
					if (_.url === path) {
						const removed = { key: _.key, url: _.url };
						const remove = () => {
							dispatch(removeComment(removed));
						};
						const save = (string: string) => {
							dispatch(updateComment({ key: _.key, url: _.url, value: string }));
						};
						Comment.render(_.key, _.value, _.url, remove, save, admin);
					}
				});
			}, 400);
		},
		//eslint-disable-next-line
		[path, document.body, comments, admin, comments.visible],
	);

	useEffect(() => {
		if (!firstLoad.current) firstLoad.current = true;
	}, [location.pathname, comments.items]);

	useEffect(() => {
		if (comments.visible) {
			window.addEventListener("resize", replaceComment, { capture: true });
			window.addEventListener("scroll", replaceComment, { capture: true });
			window.addEventListener("orientationchange", replaceComment, { capture: true });
		} else {
			Comment.removeAll();
			window.removeEventListener("resize", replaceComment, { capture: true });
			window.removeEventListener("scroll", replaceComment, { capture: true });
			window.removeEventListener("orientationchange", replaceComment, { capture: true });
		}
		return () => {
			window.removeEventListener("resize", replaceComment, { capture: true });
			window.removeEventListener("scroll", replaceComment, { capture: true });
			window.removeEventListener("orientationchange", replaceComment, { capture: true });
		};
	}, [comments.visible, replaceComment]);

	useEffect(() => {
		const click = async (event: globalThis.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			const root = document.querySelector("#root");
			if (root!.contains(event.target! as Node)) {
				let selectorComponents = [];
				let currentElement = event.target as HTMLElement;
				while (currentElement !== root) {
					if (currentElement.dataset.commentFalse !== undefined) {
						selectorComponents = [];
						break;
					}
					const tagName = currentElement.nodeName.toLowerCase();
					const id = currentElement.id ? `#${currentElement.id}` : "";
					const classes = Array.from(currentElement.classList)
						.map((className) => `[class*=${escapeSpecialChars(className)}]`)
						.join("");
					const index = getIndex(currentElement);
					selectorComponents.unshift(`${tagName}${id}${classes}:nth-child(${index})`);

					currentElement = currentElement.parentElement!;
				}
				if (selectorComponents.length) {
					const cssSelector = selectorComponents.join(" > ");
					const selectorExist = comments.items.find((_) => _.key === cssSelector);
					if (!selectorExist) {
						dispatch(createComment({ key: cssSelector, url: path, value: "" }));
					}
				}
			}
		};
		if (comments.editMode) {
			window.addEventListener("click", click, { capture: true });
		} else {
			window.removeEventListener("click", click, { capture: true });
		}
		return () => {
			window.removeEventListener("click", click, { capture: true });
		};
		//eslint-disable-next-line
	}, [comments.editMode, path, comments.items]);
	useEffect(() => {
		dispatch(loadComments(path));
		//eslint-disable-next-line
	}, [path, location.pathname]);

	useEffect(() => {
		replaceComment();
		//eslint-disable-next-line
	}, [comments, location.pathname]);
}
